// src/redux/userFollow/userFollowActionThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import UserFollowAPI from './userFollowAPI';

// Small helper to normalize Axios errors
const parseAxiosError = (err) => {
  const status = err?.response?.status ?? null;
  const detail =
    err?.response?.data?.detail ??
    err?.response?.data?.message ??
    err?.message ??
    'Request failed';
  return { status, detail };
};

// ---- FOLLOW (optimistic + idempotent) ----
export const followUserThunk = createAsyncThunk(
  'userFollow/followUser',
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      if (!userId && userId !== 0) {
        return rejectWithValue({ userId, code: 'NO_USER_ID', message: 'No userId provided' });
      }
      // prevent self follow (also guard in UI)
      const me = getState()?.auth?.user?.id ?? getState()?.user?.currentUser?.id;
      if (me && Number(me) === Number(userId)) {
        return rejectWithValue({ userId, code: 'SELF_FOLLOW', message: 'You cannot follow yourself' });
      }

      const data = await UserFollowAPI.follow(userId);
      return { userId, data, idempotent: false };
    } catch (err) {
      const { status, detail } = parseAxiosError(err);
      const msg = (detail || '').toLowerCase();

      // Treat "already following" as success (idempotent)
      // Common server patterns: 400/409 + message containing "already"
      if (status === 409 || (status === 400 && (msg.includes('already') || msg.includes('exists')))) {
        return { userId, data: null, idempotent: true };
      }

      return rejectWithValue({ userId, code: status, message: detail || 'Failed to follow user' });
    }
  }
);

// ---- UNFOLLOW (optimistic + idempotent) ----
export const unfollowUserThunk = createAsyncThunk(
  'userFollow/unfollowUser',
  async ({ userId }, { rejectWithValue }) => {
    try {
      if (!userId && userId !== 0) {
        return rejectWithValue({ userId, code: 'NO_USER_ID', message: 'No userId provided' });
      }
      const data = await UserFollowAPI.unfollow(userId);
      return { userId, data, idempotent: false };
    } catch (err) {
      const { status, detail } = parseAxiosError(err);
      const msg = (detail || '').toLowerCase();

      // Your backend example: 400 - "Unable to unfollow user or not currently followed"
      // Treat as success (idempotent) because desired end state is "not following"
      if (status === 400 && (msg.includes('not') && msg.includes('follow'))) {
        return { userId, data: null, idempotent: true };
      }

      return rejectWithValue({ userId, code: status, message: detail || 'Failed to unfollow user' });
    }
  }
);

// ---- Fetch lists ----
export const fetchFollowingThunk = createAsyncThunk(
  'userFollow/fetchFollowing',
  async ({ userId }, { rejectWithValue }) => {
    try {
      if (!userId && userId !== 0) {
        return rejectWithValue({ code: 'NO_USER_ID', message: 'No userId provided' });
      }
      const list = await UserFollowAPI.getFollowing(userId);
      return list;
    } catch (err) {
      const { status, detail } = parseAxiosError(err);
      return rejectWithValue({ code: status, message: detail || 'Failed to fetch following list' });
    }
  }
);

export const fetchFollowersThunk = createAsyncThunk(
  'userFollow/fetchFollowers',
  async ({ userId }, { rejectWithValue }) => {
    try {
      if (!userId && userId !== 0) {
        return rejectWithValue({ code: 'NO_USER_ID', message: 'No userId provided' });
      }
      const list = await UserFollowAPI.getFollowers(userId);
      return list;
    } catch (err) {
      const { status, detail } = parseAxiosError(err);
      return rejectWithValue({ code: status, message: detail || 'Failed to fetch followers list' });
    }
  }
);
